import BackClearButton from './BackClearButton';
import { TrashIcon } from './icons';
import { micErrorText } from '../utils/micErrors';
import React, { useEffect, useState, useRef } from 'react';
import { a11y } from '../utils/a11yLabels';
import DialogCard from './DialogCard';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import apiClient from '../api/client';
import { canShowPurchase } from '../utils/platform';
import { useWhisper } from '../hooks/useWhisper';
import { MicIcon, StopIcon } from './icons';

// Cached across remounts so swiping back to Note shows the list instantly
// and refreshes in the background (not persisted — fresh app open fetches once).
let tasksCache = null;
let draftCache = '';   // the half-written note, see below

// The same three colours an event uses. A note marked "urgent" and an event
// marked "urgent" must not be two different reds, or the colour stops meaning
// anything and becomes decoration.
const PRIO_COLOR = { red: '#D04535', yellow: '#E0A83E', green: '#4E7A52' };

// "No priority" — the fourth state of the bar, which has no colour and so no
// other way of announcing itself. The three that DO have colours borrow their
// words from the event form, where they are already translated.
const NO_PRIO = {
  it: 'nessuna', en: 'none', es: 'ninguna', de: 'keine', fr: 'aucune',
  nl: 'geen', pt: 'nenhuma', ru: 'нет', pl: 'brak', ro: 'niciuna',
  zh: '无', ja: 'なし', hi: 'कोई नहीं', ko: '없음', ar: 'بدون',
};

// Spoken aloud, never shown: the bar carries the meaning on screen, and a
// screen reader needs the word instead. Falls back to the raw value rather
// than to silence.
function prioName(priority, T, baseLang) {
  if (!priority) return NO_PRIO[baseLang] || NO_PRIO.en;
  const found = (T.form?.priorities || []).find(x => x.value === priority);
  return found?.label || priority;
}

// Two things this page had no words for: a list it could not read, and a note
// it could not save.
const LOCAL = {
  it: { gestures: "Tocca il testo per correggerlo · tocca la barra colorata per la priorità", editHint: 'Tocca per correggere', loadFailed: 'Non sono riuscita a leggere le tue note. Controlla la connessione.', addFailed: 'Non sono riuscita a salvare la nota. Il testo è ancora qui sotto: riprova.', keep: 'Annulla' },
  en: { gestures: "Tap the text to edit it · tap the coloured bar for priority", editHint: 'Tap to edit', loadFailed: "Couldn't read your notes. Check your connection.", addFailed: "Couldn't save the note. The text is still below — try again.", keep: 'Cancel' },
  es: { gestures: "Toca el texto para editarlo · toca la barra de color para la prioridad", editHint: 'Toca para editar', loadFailed: 'No he podido leer tus notas. Comprueba la conexión.', addFailed: 'No he podido guardar la nota. El texto sigue abajo: inténtalo de nuevo.', keep: 'Cancelar' },
  de: { gestures: "Text antippen zum Bearbeiten · farbigen Balken antippen für die Priorität", editHint: 'Zum Bearbeiten tippen', loadFailed: 'Deine Notizen konnten nicht gelesen werden. Prüfe die Verbindung.', addFailed: 'Die Notiz konnte nicht gespeichert werden. Der Text steht noch unten — versuche es erneut.', keep: 'Abbrechen' },
  fr: { gestures: "Touchez le texte pour le modifier · touchez la barre colorée pour la priorité", editHint: 'Touchez pour modifier', loadFailed: 'Impossible de lire vos notes. Vérifiez la connexion.', addFailed: "Impossible d'enregistrer la note. Le texte est toujours en bas — réessayez.", keep: 'Annuler' },
  nl: { gestures: "Tik op de tekst om te bewerken · tik op de gekleurde balk voor prioriteit", editHint: 'Tik om te bewerken', loadFailed: 'Kon je notities niet lezen. Controleer je verbinding.', addFailed: 'Kon de notitie niet opslaan. De tekst staat er nog — probeer opnieuw.', keep: 'Annuleren' },
  pt: { gestures: "Toca no texto para o editar · toca na barra colorida para a prioridade", editHint: 'Toca para editar', loadFailed: 'Não consegui ler as tuas notas. Verifica a ligação.', addFailed: 'Não consegui guardar a nota. O texto ainda está abaixo — tenta de novo.', keep: 'Cancelar' },
  ru: { gestures: "Нажмите на текст, чтобы изменить · нажмите на цветную полосу для приоритета", editHint: 'Нажмите, чтобы изменить', loadFailed: 'Не удалось прочитать ваши заметки. Проверьте соединение.', addFailed: 'Не удалось сохранить заметку. Текст остался ниже — попробуйте снова.', keep: 'Отмена' },
  pl: { gestures: "Dotknij tekstu, aby edytować · dotknij kolorowego paska, aby ustawić priorytet", editHint: 'Dotknij, aby edytować', loadFailed: 'Nie udało się odczytać notatek. Sprawdź połączenie.', addFailed: 'Nie udało się zapisać notatki. Tekst jest wciąż poniżej — spróbuj ponownie.', keep: 'Anuluj' },
  ro: { gestures: "Atinge textul pentru a-l edita · atinge bara colorată pentru prioritate", editHint: 'Atinge pentru a edita', loadFailed: 'Nu am putut citi notițele. Verifică conexiunea.', addFailed: 'Nu am putut salva notița. Textul este încă mai jos — încearcă din nou.', keep: 'Anulează' },
  zh: { gestures: "点按文字可编辑 · 点按彩色条可设置优先级", editHint: '点按以编辑', loadFailed: '无法读取你的笔记。请检查网络。', addFailed: '无法保存笔记。文字仍在下方，请重试。', keep: '取消' },
  ja: { gestures: "文字をタップして編集 · 色のバーをタップして優先度", editHint: 'タップして編集', loadFailed: 'メモを読み込めませんでした。接続を確認してください。', addFailed: 'メモを保存できませんでした。文章は下に残っています。もう一度お試しください。', keep: 'キャンセル' },
  hi: { gestures: "बदलने के लिए टेक्स्ट पर टैप करें · प्राथमिकता के लिए रंगीन पट्टी पर टैप करें", editHint: 'संपादित करने के लिए टैप करें', loadFailed: 'आपके नोट नहीं पढ़े जा सके। कनेक्शन जाँचें।', addFailed: 'नोट सहेजा नहीं जा सका। टेक्स्ट नीचे मौजूद है — फिर कोशिश करें।', keep: 'रद्द करें' },
  ko: { gestures: "글을 탭하면 수정 · 색 막대를 탭하면 우선순위", editHint: '탭하여 수정', loadFailed: '메모를 불러오지 못했습니다. 연결을 확인하세요.', addFailed: '메모를 저장하지 못했습니다. 글은 아래 그대로 있습니다. 다시 시도하세요.', keep: '취소' },
  ar: { gestures: "اضغط على النص لتعديله · اضغط على الشريط الملوّن لتحديد الأولوية", editHint: 'اضغط للتعديل', loadFailed: 'تعذّرت قراءة ملاحظاتك. تحقق من الاتصال.', addFailed: 'تعذّر حفظ الملاحظة. النص لا يزال بالأسفل — حاول مجددًا.', keep: 'إلغاء' },
};

export default function TasksPage() {
  const { user } = useAuth();
  const lang = user?.language || 'it';
  const T = useT(lang);
  const baseLang = lang.split('-')[0];
  const L = LOCAL[baseLang] || LOCAL.en;
  const A = a11y(lang);   // spoken name for the priority bar

  const [tasks,         setTasks]         = useState(() => tasksCache || []);
  const [loading,       setLoading]       = useState(() => !tasksCache);
  // What is half-typed in the box, kept across a trip to another tab.
  //
  // Notes is a bottom-bar destination, so leaving it is one accidental tap —
  // and everything typed vanished with no warning and no way back. The event
  // form was given a guard for exactly this; a note is smaller but it is the
  // same loss, and the same surprise. Kept in the module (like the list cache
  // just above) rather than in storage: it should survive a wandering thumb,
  // not outlive the app.
  const [newTaskTitle,  setNewTaskTitle]  = useState(() => draftCache);
  const [saving,        setSaving]        = useState(false);
  const [tasksScope,    setTasksScope]    = useState(true);
  const [noteError,     setNoteError]     = useState(''); // shows the real server error if a Done/Delete fails
  const [loadFailed,    setLoadFailed]    = useState(false); // read failed ≠ no notes
  const [confirmDelete, setConfirmDelete] = useState(null);  // task id awaiting confirmation
  // Correcting a note you typed wrong. Until now the only way was to delete it
  // and write it again, which is not an edit — it is a punishment for a typo.
  const [editingId,     setEditingId]     = useState(null);
  const [editText,      setEditText]      = useState('');
  // Escape has to beat the blur that follows it.
  //
  // Leaving the field fires onBlur, and onBlur saves. Pressing Escape removes
  // the field, which ALSO fires a blur — carrying the text as it was a moment
  // ago. So "cancel" would have saved the very edit it was cancelling, and only
  // sometimes, depending on which happened first. A ref, not state: it has to
  // be true for the blur running in this same tick, and a state update is not.
  const cancelEditRef = useRef(false);

  const {
    recording: micRecording,
    transcribing: micTranscribing,
    startRecording: micStart,
    stopRecording: micStop,
    error: micError,
  } = useWhisper(lang);

  function handleMicToggle() {
    if (micTranscribing) return;
    if (micRecording) { micStop(); return; }
    micStart((text) => setNewTaskTitle(v => v ? v + ' ' + text : text));
  }

  // Grow the note box as the text wraps onto new lines (capped, then scrolls).
  const inputRef = useRef(null);
  const autoSize = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 96) + 'px';
    el.style.overflowY = el.scrollHeight > 96 ? 'auto' : 'hidden';
  };
  useEffect(() => { autoSize(inputRef.current); }, [newTaskTitle]);

  // Write-through: keep the module cache in sync with the list after add/delete,
  // so swiping back to Note shows the fresh list, not a stale cached one.
  useEffect(() => { tasksCache = tasks; }, [tasks]);
  useEffect(() => { draftCache = newTaskTitle; }, [newTaskTitle]);

  useEffect(() => {
    apiClient.get('/tasks', { params: { date: '2099-12-31' } })
      .then(r => { tasksCache = r.data || []; setTasks(tasksCache); setTasksScope(true); setLoading(false); })
      .catch(err => {
        // 403 is an answer: this account cannot see notes. Anything else means
        // we could not read them — which is NOT the same as "you have none",
        // and showing an empty page for it made people think their notes had
        // been lost. Keep whatever we had and say what happened.
        if (err.response?.status === 403) { setTasksScope(false); setTasks([]); }
        else setLoadFailed(true);
        setLoading(false);
      });
  }, []);

  /**
   * Save a corrected note.
   *
   * Optimistic, and it puts the old words back if the save fails — the note
   * stays on screen either way, so an edit that did not reach the server never
   * looks like it did.
   */
  async function saveEdit(task) {
    // Escape got here first — leave everything exactly as it was.
    if (cancelEditRef.current) { cancelEditRef.current = false; return; }
    const next = editText.trim();
    setEditingId(null);
    setEditText('');
    if (!next || next === task.title) return;   // nothing to do, and never empty
    const before = task.title;
    setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, title: next } : t)));
    try {
      await apiClient.patch(`/tasks/${task.id}`, { title: next });
    } catch (err) {
      setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, title: before } : t)));
      setNoteError(err?.response?.data?.error || err?.message || L.addFailed);
    }
  }

  /** red → yellow → green → none → red. Optimistic, reverts on failure. */
  async function handleCyclePriority(task) {
    const order = [null, 'red', 'yellow', 'green'];
    const next = order[(order.indexOf(task.priority ?? null) + 1) % order.length];
    const before = task.priority ?? null;
    setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, priority: next } : t)));
    try {
      await apiClient.patch(`/tasks/${task.id}`, { priority: next });
    } catch (err) {
      setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, priority: before } : t)));
      setNoteError(err?.response?.data?.error || err?.message || L.addFailed);
    }
  }

  async function handleAddTask(e) {
    e?.preventDefault();
    const title = newTaskTitle.trim();
    if (!title) return;
    setSaving(true);
    setNewTaskTitle('');
    const tempId = 'temp-' + Date.now();
    setTasks(prev => [{ id: tempId, title, status: 'needsAction' }, ...prev]);
    try {
      const res = await apiClient.post('/tasks', { title });
      setTasks(prev => prev.map(t => t.id === tempId ? res.data : t));
    } catch (e) {
      // The note used to appear and then vanish, with nothing said. Give the
      // words back to the box so nothing typed is lost, and say why.
      setTasks(prev => prev.filter(t => t.id !== tempId));
      setNewTaskTitle(title);
      setNoteError(e?.response?.data?.error || e?.message || L.addFailed);
    }
    setSaving(false);
  }

  // First ✕ on an active note: mark it done (strikethrough → "Eseguiti"),
  // not deleted. Optimistic; reverts on error.
  function handleCompleteTask(taskId) {
    setNoteError('');
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: 'completed' } : t));
    apiClient.patch(`/tasks/${taskId}`, { status: 'completed' })
      .catch((e) => {
        setNoteError(e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'errore');
        setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: 'needsAction' } : t));
      });
  }

  // Tap a done note to bring it back to the active list.
  function handleRestoreTask(taskId) {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: 'needsAction' } : t));
    apiClient.patch(`/tasks/${taskId}`, { status: 'needsAction' })
      .catch(() => setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: 'completed' } : t)));
  }

  // Second ✕ (on a done note): permanent delete, with confirmation.
  function handleDeleteTask(taskId) {
    setNoteError('');
    setConfirmDelete(taskId);   // asked in the app's own voice, see the modal
  }

  function doDeleteTask(taskId) {
    setConfirmDelete(null);
    const snapshot = tasks;
    setTasks(prev => prev.filter(t => t.id !== taskId));
    apiClient.delete(`/tasks/${taskId}`).catch((e) => {
      setNoteError(e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'errore');
      setTasks(snapshot); // restore if the server refused the delete
    });
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0 10px' }}>
        <BackClearButton
          hasText={!!newTaskTitle.trim()}
          onClear={() => setNewTaskTitle('')}
          label={lang}
        />
      </div>
      {/* Scrollable content wrapper */}
      {/* Permanent delete, asked in the app's own voice — the last window.confirm
          on this page, on the only action here that cannot be undone. */}
      {confirmDelete && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }} onClick={() => setConfirmDelete(null)}>
          <DialogCard onClick={e => e.stopPropagation()} style={{
            background: 'var(--color-card)', borderRadius: 'var(--radius)',
            padding: '22px 20px', width: '100%', maxWidth: 340,
            boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
          }}>
            <p style={{ margin: '0 0 18px', fontWeight: 700, fontSize: '0.95rem', color: 'var(--color-text)', lineHeight: 1.45 }}>
              {T.tasks?.deleteNoteConfirm || 'Delete this note permanently?'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button type="button" className="btn btn-outline" onClick={() => setConfirmDelete(null)}>{L.keep}</button>
              <button type="button" className="btn btn-outline"
                style={{ borderColor: 'var(--color-red)', color: 'var(--color-red)' }}
                onClick={() => doDeleteTask(confirmDelete)}>
                {T.form?.deleteButton || 'Delete'}
              </button>
            </div>
          </DialogCard>
        </div>
      )}

      <div style={{ paddingBottom: 190 }}>
        {loading && <div className="spinner" />}

        {loadFailed && !loading && (
          <div className="alert alert-error" style={{ textAlign: 'center' }}>
            {L.loadFailed}
          </div>
        )}

        {noteError && (
          <div style={{ background: '#fdecea', border: '1px solid #f5b7b1', color: '#c0392b', borderRadius: 8, padding: '8px 10px', margin: '4px 0 10px', fontSize: '0.78rem', wordBreak: 'break-word' }}>
            ⚠️ {noteError}
          </div>
        )}

        {!loading && !tasksScope && (
          <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', padding: '8px 0' }}>
            {T.tasks?.noScope}
          </div>
        )}

        {!loading && !loadFailed && tasksScope && tasks.length === 0 && (
          <div className="empty-state">
            <span className="empty-state-icon">📝</span>
            <span className="empty-state-title">{T.tasks?.emptyTitle}</span>
            <span className="empty-state-sub" style={{ fontStyle: 'italic', opacity: 0.85 }}>{T.tasks?.emptySub}</span>
          </div>
        )}

        {/* Both new gestures, said once, above the list they apply to.
            A feature nobody is told about is a feature nobody has: tapping a
            thin coloured bar is not something anyone tries on a hunch. Shown
            only when there is at least one note, because on an empty page it
            would be instructions for something that isn't there. */}
        {!loading && !loadFailed && tasksScope && tasks.some(t => t.status !== 'completed') && (
          <p style={{
            margin: '0 0 8px', fontSize: '0.74rem', lineHeight: 1.45,
            color: 'var(--color-text-muted)',
            display: 'flex', gap: 6, alignItems: 'baseline',
          }}>
            {/* An arrow, then the words in quotes. Plain grey text above a list
                reads as a caption and gets skipped; this reads as somebody
                pointing at something. The arrow follows the writing direction,
                so in Arabic it points the other way — an arrow aimed off the
                edge of the sentence is worse than none. */}
            <span aria-hidden="true" style={{ flexShrink: 0 }}>
              {baseLang === 'ar' ? '←' : '→'}
            </span>
            <span>«{L.gestures}»</span>
          </p>
        )}

        {/* ── Active notes ─────────────────────────────────────────────── */}
        {tasks.filter(t => t.status !== 'completed').map(task => (
          <div key={task.id} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 4px',
            borderBottom: '1px solid var(--color-border)',
          }}>
            {/* The priority bar — the same shape and the same three colours as
                an event's, because it means the same thing and there is no
                reason to make somebody learn it twice. Tapping cycles
                red → yellow → green → none. */}
            <button
              type="button"
              onClick={() => handleCyclePriority(task)}
              aria-label={`${A.priority}: ${prioName(task.priority, T, baseLang)}`}
              className="tap-grow"
              style={{
                '--tap-x': '5px',
                width: 5, height: 30, flexShrink: 0, padding: 0,
                borderRadius: 3, border: 'none', cursor: 'pointer',
                background: task.priority ? PRIO_COLOR[task.priority] : 'var(--color-border)',
              }}
            />

            {editingId === task.id ? (
              <input
                autoFocus
                value={editText}
                onChange={e => setEditText(e.target.value)}
                onBlur={() => saveEdit(task)}
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                  // Escape puts back what was there. Without it the only way out
                  // of an edit begun by accident was to save it.
                  if (e.key === 'Escape') { cancelEditRef.current = true; setEditingId(null); setEditText(''); }
                }}
                style={{
                  flex: 1, minWidth: 0, fontSize: '0.92rem', fontWeight: 700,
                  fontFamily: 'inherit', color: 'var(--color-text)',
                  background: 'var(--color-card)',
                  // The border takes the note's own priority colour, so the frame
                  // that appears under your finger is the same colour as the bar
                  // beside it. A note with no priority keeps the neutral border —
                  // grey means 'none' here exactly as it does on the bar, which
                  // is why it is the border colour and not the app's green.
                  border: '2px solid ' + (task.priority ? PRIO_COLOR[task.priority] : 'var(--color-border)'),
                  borderRadius: 8,
                  padding: '6px 8px',
                }}
              />
            ) : (
              <button
                type="button"
                onClick={() => { setEditingId(task.id); setEditText(task.title); }}
                title={L.editHint}
                style={{
                  flex: 1, minWidth: 0, textAlign: 'start',
                  fontSize: '0.92rem', fontWeight: 700, color: 'var(--color-text)',
                  background: 'none', border: 'none', padding: '6px 0',
                  fontFamily: 'inherit', cursor: 'text',
                }}
              >
                {task.title}
              </button>
            )}

            {/* First ✕ → mark done (not delete) */}
            <button
              type="button"
              onClick={() => handleCompleteTask(task.id)}
              style={{
                width: 44, height: 44, flexShrink: 0,
                background: 'none', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--color-primary)',
              }}
              aria-label={T.tasks?.doneSection}
            ><svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true" stroke="currentColor" fill="none" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4.5 4.5L19 7"/></svg></button>
          </div>
        ))}

        {/* ── "Eseguiti" (done) section ────────────────────────────────── */}
        {tasks.some(t => t.status === 'completed') && (
          <>
            <div style={{
              marginTop: 18, marginBottom: 2, padding: '4px 4px',
              fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.06em', color: 'var(--color-text-muted)',
            }}>
              {T.tasks?.doneSection || 'Done'}
            </div>
            {tasks.filter(t => t.status === 'completed').map(task => (
              <div key={task.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 4px',
                borderBottom: '1px solid var(--color-border)',
              }}>
                {/* Done note: normal weight, dark grey (tap the text to restore it) */}
                <span
                  onClick={() => handleRestoreTask(task.id)}
                  role="button"
                  tabIndex={0}
                  title={T.tasks?.restoreNote}
                  aria-label={`${task.title} — ${T.tasks?.restoreNote || ''}`}
                  style={{
                    flex: 1, fontSize: '0.92rem', fontWeight: 400, color: 'var(--color-text-muted)',
                    cursor: 'pointer',
                  }}
                >
                  {task.title}
                </span>

                {/* Second ✕ → permanent delete (asks for confirmation) */}
                <button
                  type="button"
                  onClick={() => handleDeleteTask(task.id)}
                  style={{
                    width: 44, height: 44, flexShrink: 0,
                    background: 'none', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'var(--color-primary)',
                  }}
                  aria-label={T.form?.deleteButton}
                ><TrashIcon size={17} /></button>
              </div>
            ))}
          </>
        )}
      </div>

      {/* ── Fixed bottom bar — sage-bordered box + vertical "+"-style buttons ── */}
      <div style={{
        position: 'fixed',
        bottom: 'calc(64px + env(safe-area-inset-bottom, 0px))', left: 0, right: 0,
        maxWidth: 480, margin: '0 auto',
        zIndex: 100,
        padding: '8px 14px',
      }}>
        {/* Note box (sage border, wraps) on the left; "+" and mic set apart on
            the right, stacked vertically and styled like the "+" page FABs. */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 22 }}>

          {/* Text box — sage border, grows as the text wraps */}
          <form onSubmit={handleAddTask} style={{
            flex: 1, display: 'flex', alignItems: 'flex-end', gap: 8,
            background: 'var(--color-card)',
            borderRadius: 'var(--radius-sm)', padding: '0 12px',
            border: '2px solid var(--color-primary)', minWidth: 0,
          }}>
            <textarea
              ref={inputRef}
              value={newTaskTitle}
              onChange={e => setNewTaskTitle(e.target.value)}
              placeholder={T.tasks?.addPlaceholder || 'Scrivi una nota…'}
              aria-label={T.tasks?.addPlaceholder || 'Scrivi una nota…'}
              autoComplete="off"
              rows={1}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddTask(e); }
              }}
              style={{
                flex: 1, border: 'none', background: 'transparent',
                fontSize: '1rem', padding: '11px 0',
                color: 'var(--color-text)', outline: 'none',
                fontFamily: 'inherit', minWidth: 0,
                resize: 'none', overflowY: 'hidden', maxHeight: 96, lineHeight: 1.35,
              }}
            />
          </form>

          {/* Vertical stack: add (+) on top, mic below — like the "+" page */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center', flexShrink: 0 }}>
            {/* Round add button */}
            <button
              type="button"
              onClick={handleAddTask}
              disabled={saving}
              aria-label={T.settings?.saveButton || 'OK'}
              style={{
                width: 64, height: 64, borderRadius: '50%',
                border: 'none', background: 'var(--color-primary)', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                // The same 64px circle and the same 1.12rem as the OK in the
                // + — they are the same button doing the same job, and they
                // must not drift apart again.
                lineHeight: 1, fontSize: '1.12rem', fontWeight: 800, letterSpacing: '0.03em',
                cursor: saving ? 'not-allowed' : 'pointer',
                boxShadow: '0 3px 12px rgba(78,122,82,0.45)',
              }}
            >
              {saving ? '⏳' : <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" stroke="currentColor" fill="none" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4.5 4.5L19 7"/></svg>}
            </button>

            {/* Round mic button (same look as the "+" page) */}
            <button
              type="button"
              onClick={handleMicToggle}
              disabled={micTranscribing}
              aria-label={T.today?.microphone}
              style={{
                width: 64, height: 64, borderRadius: '50%',
                border: 'none', color: '#fff',
                background: (micRecording || micTranscribing) ? '#c4614a' : 'var(--color-primary)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1.9rem', lineHeight: 1,
                boxShadow: '0 3px 12px rgba(78,122,82,0.45)',
                cursor: micTranscribing ? 'not-allowed' : 'pointer',
                transition: 'background 0.15s',
              }}
            >
              {micTranscribing ? '…' : micRecording ? <StopIcon /> : <MicIcon />}
            </button>
          </div>
        </div>
        {micError === 'ai-limit' && (
          <div style={{ color: 'var(--color-primary-dark)', fontSize: '0.78rem', textAlign: 'center', padding: '6px 6px 2px' }}>
            {canShowPurchase()
              ? <a href="/settings?upgrade=1" style={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: '3px' }}>{T.today?.aiLimitReached} ⭐</a>
              /* In the Play Store app: state the fact, offer no way to buy. */
              : <span>{T.today?.aiLimitReached}</span>}
          </div>
        )}
        {micError && micError !== 'ai-limit' && (
          <div role="alert" style={{ color: '#c0392b', fontSize: '0.78rem', textAlign: 'center', padding: '6px 6px 2px', lineHeight: 1.4 }}>
            {micErrorText(micError, lang)}
          </div>
        )}
      </div>
    </>
  );
}
